import { useEffect, useState, KeyboardEvent } from 'react';
import { useNotification } from '@/components/notifications';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Org } from '@/types';
import { apiRequest } from '@/utils/api-request';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

interface AdminBrandingComposerSettingsProps {
  org: Org;
  onUpdate: (updates: Partial<Org>) => void;
}

export const AdminBrandingComposerSettings = ({
  org,
  onUpdate,
}: AdminBrandingComposerSettingsProps) => {
  const [composerPageTitle, setComposerPageTitle] = useState<string>(
    org?.branding?.workflow_page_title || '',
  );
  const notification = useNotification();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setComposerPageTitle(org?.branding?.workflow_page_title || '');
  }, [org]);

  const handleSave = async () => {
    const trimmedTitle = composerPageTitle.trim();

    if (trimmedTitle.length > 200) {
      notification.error('Composer page title cannot exceed 200 characters');
      return;
    }

    setIsLoading(true);
    try {
      const updatedBranding = {
        ...org?.branding,
        workflow_page_title: trimmedTitle,
      };

      const response = await apiRequest({
        url: `/organizations/${org.orgid}`,
        method: 'PUT',
        target: 'platform',
        body: {
          name: org.name,
          branding: updatedBranding,
        },
      });

      if (response?.detail) {
        notification.error(response.detail);
        return;
      }

      notification.success('Composer settings updated successfully');
      onUpdate({
        branding: {
          ...org?.branding,
          workflow_page_title: trimmedTitle,
        },
      });
    } catch (error) {
      console.error('Update composer settings error:', error);
      notification.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSave();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Composer Settings</CardTitle>
      </CardHeader>
      <CardContent className='flex flex-col gap-6'>
        {/* Composer Page Title */}
        <div className='space-y-2'>
          <Label htmlFor='composer-page-title'>Composer page title</Label>
          <Input
            id='composer-page-title'
            value={composerPageTitle}
            onChange={(e) => setComposerPageTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='Enter composer page title (e.g., Composer, Workflows)'
          />
        </div>

        <Button onClick={handleSave} disabled={isLoading} className='w-fit'>
          {isLoading ? 'Saving...' : 'Save Title'}
        </Button>
      </CardContent>
    </Card>
  );
};
